// Landed cost (ERP-PLAN §5.6 step 5), shared by the browser (the Costs tab works it out as you type) and the
// server (landed_cost.apply writes exactly what the screen showed). Charges from a forwarder's invoice are
// converted to USD on their own rate, then split over the goods they brought in by value, weight or
// quantity, in whole cents with the largest-remainder method, so each charge's shares add up to it exactly.
import { z } from "zod";
import { mulRound } from "./po.ts";
import { keepsLedger, postsToShopify } from "./receiving.ts";

export const CHARGE_KINDS = ["freight", "duty", "tariff", "fee", "other"] as const;
export type ChargeKind = (typeof CHARGE_KINDS)[number];
export const CHARGE_KIND_LABELS: Record<ChargeKind, string> = { freight: "Freight", duty: "Duty", tariff: "Tariff", fee: "Fee", other: "Other" };

export const ALLOCATION_METHODS = ["value", "weight", "qty"] as const;
export type AllocationMethod = (typeof ALLOCATION_METHODS)[number];
export const ALLOCATION_LABELS: Record<AllocationMethod, string> = { value: "By value", weight: "By weight", qty: "By quantity" };

/** One line of goods a landed cost covers: a receipt line (good units), or before anything arrives, a PO line as ordered. */
export type BasisLine = {
  key: string;
  po_id: string;
  po_number: string;
  po_line_id: string;
  receipt_id: string | null;
  receipt_number: string;
  item_id: string;
  sku: string;
  name: string;
  qty: number;
  unit_price: number | null; // in `currency`
  currency: string;
  weight_g: number | null;
  /** A local item: its receipt moves get the cost in the ledger. */
  ledger: boolean;
  /** A Shopify item: its unit cost can be updated in Shopify. */
  shopify: boolean;
};

export type ChargeInput = {
  id: string;
  position: number;
  kind: string;
  description: string;
  amount: number;
  currency: string;
  usd_rate: number;
  method: AllocationMethod;
};

export type Rates = Record<string, number>;

const cents = (usd: number) => Math.round(mulRound(usd, 100, 6));
/** USD for an amount in `currency` (1 for USD, else the rate given), to the cent. */
export const toUsd = (amount: number, currency: string, rate: number) =>
  currency.toUpperCase() === "USD" ? mulRound(amount, 1, 2) : mulRound(amount, rate, 2);
/** A charge in USD, to the cent. */
export const chargeUsd = (c: Pick<ChargeInput, "amount" | "currency" | "usd_rate">) => toUsd(Number(c.amount), c.currency, Number(c.usd_rate));

/** USD per unit of goods: the PO price at the landed cost's rate for its currency, or null without a price or rate. */
export function goodsUnitUsd(line: Pick<BasisLine, "unit_price" | "currency">, rates: Rates): number | null {
  if (line.unit_price == null) return null;
  if (line.currency.toUpperCase() === "USD") return Number(line.unit_price);
  const rate = Number(rates[line.currency.toUpperCase()]);
  return rate > 0 ? mulRound(Number(line.unit_price), rate, 6) : null;
}

/**
 * Splits `total` cents over `weights` so the parts add up to it exactly: each part gets the whole cents of its
 * share, then the cents left over go one each to the biggest fractions (the earlier line on a tie). A negative
 * total (a credit) is split the same way and comes back negative.
 */
export function splitCents(total: number, weights: number[]): number[] {
  const sum = weights.reduce((a, b) => a + b, 0);
  if (!weights.length || !(sum > 0)) return weights.map(() => 0);
  const sign = total < 0 ? -1 : 1;
  const abs = Math.abs(Math.round(total));
  const raw = weights.map((w) => {
    const r = (abs * w) / sum;
    const near = Math.round(r);
    return Math.abs(r - near) < 1e-7 ? near : r; // 299.99999999 is 300
  });
  const parts = raw.map(Math.floor);
  let left = abs - parts.reduce((a, b) => a + b, 0);
  const order = raw.map((r, i) => ({ i, frac: r - Math.floor(r) })).sort((a, b) => b.frac - a.frac || a.i - b.i);
  for (let k = 0; left > 0 && k < order.length; k++, left--) parts[order[k].i] += 1;
  return parts.map((p) => sign * p);
}

export type ChargeResult = {
  id: string;
  usd: number;
  /** How it was actually split: weight or value fall back to quantity when a line can't be weighed or valued. */
  method: AllocationMethod;
  flag: string;
  /** Cents per basis line, in basis order. */
  cents: number[];
};

export type LineResult = BasisLine & {
  goods_unit_usd: number | null;
  goods_usd: number | null;
  /** USD of charges on this line, total and per charge id. */
  charges_usd: number;
  by_charge: Record<string, number>;
  extra_unit_usd: number;
  landed_unit_usd: number | null;
  landed_usd: number | null;
};

export type Allocation = {
  lines: LineResult[];
  charges: ChargeResult[];
  charges_usd: number;
  goods_usd: number | null;
  landed_usd: number | null;
  /** Currencies with goods but no rate yet (their value and landed cost are unknown). */
  missing_rates: string[];
  flags: string[];
};

const who = (l: BasisLine) => l.sku || l.name;
const named = (lines: BasisLine[]) => {
  const names = [...new Set(lines.map(who))];
  return names.length > 3 ? `${names.slice(0, 3).join(", ")} and ${names.length - 3} more` : names.join(", ");
};

/** The charges split over the lines, and each line's landed unit cost in USD. */
export function allocate(lines: BasisLine[], charges: ChargeInput[], rates: Rates): Allocation {
  const units = lines.map((l) => Math.max(0, Number(l.qty)));
  const unitUsd = lines.map((l) => goodsUnitUsd(l, rates));
  const sorted = [...charges].sort((a, b) => a.position - b.position || a.id.localeCompare(b.id));
  const flags: string[] = [];
  const missing = [...new Set(lines.filter((l, i) => l.unit_price != null && unitUsd[i] == null).map((l) => l.currency.toUpperCase()))];
  if (missing.length) flags.push(`Enter a USD rate for ${missing.join(", ")} to value the goods`);

  const results: ChargeResult[] = sorted.map((c) => {
    const usd = chargeUsd(c);
    let method: AllocationMethod = c.method;
    let flag = "";
    let weights = units;
    if (method === "value") {
      const unknown = lines.filter((_, i) => unitUsd[i] == null);
      const values = lines.map((_, i) => units[i] * (unitUsd[i] ?? 0));
      if (unknown.length) {
        method = "qty";
        flag = `No value for ${named(unknown)} (no price or rate), so split by quantity`;
      } else if (values.reduce((a, b) => a + b, 0) <= 0) {
        method = "qty";
        flag = "The goods have no value, so split by quantity";
      } else weights = values;
    } else if (method === "weight") {
      const unweighed = lines.filter((l) => !(Number(l.weight_g) > 0));
      if (unweighed.length) {
        method = "qty";
        flag = `No weight for ${named(unweighed)}, so split by quantity`;
      } else weights = lines.map((l, i) => units[i] * Number(l.weight_g));
    }
    if (lines.length && !weights.some((w) => w > 0)) flag = "Nothing to split it over";
    return { id: c.id, usd, method, flag, cents: splitCents(cents(usd), weights) };
  });
  for (const r of results) if (r.flag) flags.push(r.flag);

  const out: LineResult[] = lines.map((l, i) => {
    const by_charge = Object.fromEntries(results.map((r) => [r.id, r.cents[i] / 100]));
    const chargeCents = results.reduce((n, r) => n + r.cents[i], 0);
    const qty = units[i];
    const goods = unitUsd[i] == null ? null : mulRound(qty, unitUsd[i] as number, 2);
    const extra = qty > 0 ? mulRound(chargeCents / 100 / qty, 1, 4) : 0;
    const landedTotal = goods == null ? null : mulRound(goods + chargeCents / 100, 1, 2);
    return {
      ...l,
      goods_unit_usd: unitUsd[i],
      goods_usd: goods,
      charges_usd: chargeCents / 100,
      by_charge,
      extra_unit_usd: extra,
      landed_unit_usd: landedTotal == null || qty <= 0 ? null : mulRound(landedTotal / qty, 1, 4),
      landed_usd: landedTotal,
    };
  });
  const sumCents = (ns: number[]) => ns.reduce((a, b) => a + b, 0) / 100;
  const known = out.every((l) => l.goods_usd != null);
  return {
    lines: out,
    charges: results,
    charges_usd: sumCents(results.map((r) => cents(r.usd))),
    goods_usd: known ? sumCents(out.map((l) => cents(l.goods_usd as number))) : null,
    landed_usd: known ? sumCents(out.map((l) => cents(l.landed_usd as number))) : null,
    missing_rates: missing,
    flags,
  };
}

// Basis ------------------------------------------------------------------------------------------------

type BasisPo = { id: string; number: string; currency: string };
type BasisPoLine = { id: string; po_id: string; position: number; item_id: string; sku: string; name: string; qty: number; unit_price: number | null };
type BasisReceipt = { id: string; po_id: string; number: string; received_at: string | Date };
type BasisReceiptLine = { id: string; receipt_id: string; po_line_id: string; item_id: string; qty_received: number };
type BasisItem = {
  id: string;
  source: string;
  kind: string;
  weight_g: number | null;
  shopify_inventory_item_id: string | null;
  shopify_tracked: boolean | null;
};

/**
 * The receipts a landed cost covers: scope 'po' is every receipt of its PO, scope 'receipts' the ones picked.
 * `chosen` is the landed_cost_receipt rows' receipt ids.
 */
export function receiptsInScope<R extends BasisReceipt>(lc: { scope: string; po_id: string | null }, chosen: string[], receipts: R[]): R[] {
  const picked = new Set(chosen);
  return receipts
    .filter((r) => (lc.scope === "po" ? r.po_id === lc.po_id : picked.has(r.id)))
    .sort((a, b) => String(a.number).localeCompare(String(b.number), undefined, { numeric: true }));
}

/**
 * The goods a landed cost is split over: the good units on its receipts' lines, or for a PO nothing has arrived
 * for yet, its lines as ordered (an estimate: it can't be applied until goods arrive).
 */
export function basisLines(
  lc: { scope: string; po_id: string | null },
  data: { receipts: BasisReceipt[]; receiptLines: BasisReceiptLine[]; poLines: BasisPoLine[]; pos: BasisPo[]; items: BasisItem[] },
): { lines: BasisLine[]; estimate: boolean } {
  const poById = new Map(data.pos.map((p) => [p.id, p]));
  const lineById = new Map(data.poLines.map((l) => [l.id, l]));
  const itemById = new Map(data.items.map((i) => [i.id, i]));
  const facts = (itemId: string) => {
    const i = itemById.get(itemId);
    return {
      weight_g: i?.weight_g == null ? null : Number(i.weight_g),
      ledger: i ? keepsLedger(i) : false,
      shopify: i ? postsToShopify(i) : false,
    };
  };
  const receipts = new Map(data.receipts.map((r) => [r.id, r]));
  const rows = data.receiptLines
    .filter((rl) => receipts.has(rl.receipt_id) && Number(rl.qty_received) > 0)
    .flatMap((rl) => {
      const r = receipts.get(rl.receipt_id) as BasisReceipt;
      const pl = lineById.get(rl.po_line_id);
      const po = poById.get(r.po_id);
      if (!pl || !po) return [];
      return [
        {
          sort: [String(r.number), pl.position] as const,
          line: {
            key: rl.id,
            po_id: po.id,
            po_number: po.number,
            po_line_id: pl.id,
            receipt_id: r.id,
            receipt_number: r.number,
            item_id: rl.item_id,
            sku: pl.sku,
            name: pl.name,
            qty: Number(rl.qty_received),
            unit_price: pl.unit_price == null ? null : Number(pl.unit_price),
            currency: po.currency,
            ...facts(rl.item_id),
          },
        },
      ];
    })
    .sort((a, b) => a.sort[0].localeCompare(b.sort[0], undefined, { numeric: true }) || a.sort[1] - b.sort[1])
    .map((x) => x.line);
  if (rows.length || lc.scope !== "po" || !lc.po_id) return { lines: rows, estimate: false };
  const po = poById.get(lc.po_id);
  if (!po) return { lines: [], estimate: false };
  const ordered = data.poLines
    .filter((l) => l.po_id === po.id)
    .sort((a, b) => a.position - b.position)
    .map((pl) => ({
      key: pl.id,
      po_id: po.id,
      po_number: po.number,
      po_line_id: pl.id,
      receipt_id: null,
      receipt_number: "",
      item_id: pl.item_id,
      sku: pl.sku,
      name: pl.name,
      qty: Number(pl.qty),
      unit_price: pl.unit_price == null ? null : Number(pl.unit_price),
      currency: po.currency,
      ...facts(pl.item_id),
    }));
  return { lines: ordered, estimate: ordered.length > 0 };
}

// Ledger -------------------------------------------------------------------------------------------------

/** A local receipt move (stock_move, reason receipt). */
export type LedgerMove = { id: string; item_id: string; ref_id: string | null; qty: number; unit_cost: number | null };
export type CostAdjustment = { stock_move_id: string; landed_cost_id: string; kind: "goods" | "charges"; amount: number };

/**
 * What the ledger should hold for this landed cost, per receipt move and kind: each local line's charges (and,
 * for a move without a unit cost because the PO wasn't in USD, its goods at the landed cost's rate), spread
 * over the line's moves by quantity. Applying writes the difference from what's there already.
 */
export function ledgerTargets(
  alloc: Allocation,
  moves: LedgerMove[],
): { stock_move_id: string; item_id: string; kind: "goods" | "charges"; amount: number; qty: number }[] {
  const out: { stock_move_id: string; item_id: string; kind: "goods" | "charges"; amount: number; qty: number }[] = [];
  const groups = new Map<string, { charges: number; goods: number | null; unit: number | null; moves: LedgerMove[] }>();
  for (const l of alloc.lines) {
    if (!l.ledger || !l.receipt_id) continue;
    const key = `${l.receipt_id}:${l.item_id}`;
    const g = groups.get(key) ?? {
      charges: 0,
      goods: 0,
      unit: l.goods_unit_usd,
      moves: moves.filter((m) => m.ref_id === l.receipt_id && m.item_id === l.item_id),
    };
    g.charges += cents(l.charges_usd);
    g.goods = g.goods == null || l.goods_usd == null ? null : g.goods + cents(l.goods_usd);
    groups.set(key, g);
  }
  for (const g of groups.values()) {
    if (!g.moves.length) continue;
    const qtys = g.moves.map((m) => Number(m.qty));
    const charges = splitCents(g.charges, qtys);
    const goods = g.goods == null ? null : splitCents(g.goods, qtys);
    g.moves.forEach((m, i) => {
      out.push({ stock_move_id: m.id, item_id: m.item_id, kind: "charges", amount: charges[i] / 100, qty: Number(m.qty) });
      if (m.unit_cost == null && goods) out.push({ stock_move_id: m.id, item_id: m.item_id, kind: "goods", amount: goods[i] / 100, qty: Number(m.qty) });
    });
  }
  return out;
}

/** A receipt move's unit cost in USD with its cost adjustments, or null when its goods cost is unknown. */
export function moveUnitCost(move: LedgerMove, adjustments: Pick<CostAdjustment, "stock_move_id" | "kind" | "amount">[]): number | null {
  const mine = adjustments.filter((a) => a.stock_move_id === move.id);
  const hasGoods = move.unit_cost != null || mine.some((a) => a.kind === "goods");
  const qty = Number(move.qty);
  if (!hasGoods || qty <= 0) return null;
  const total = mulRound(Number(move.unit_cost ?? 0), qty, 6) + mine.reduce((n, a) => n + Number(a.amount), 0);
  return mulRound(total / qty, 1, 4);
}

// Shopify unit cost ----------------------------------------------------------------------------------------

export type ShopifyCostRow = {
  item_id: string;
  sku: string;
  name: string;
  /** Shopify's unit cost now. */
  current: number | null;
  /** The cost the average starts from: now, or for an item this landed cost already updated, the cost before it did. */
  base: number | null;
  /** This landed cost already updated this item's cost (re-running starts from `base`, so it never compounds). */
  applied: boolean;
  landed_unit: number;
  received: number;
  on_hand: number;
  /** Units already in stock before these arrived (on hand less what arrived, never below 0). */
  older: number;
  next: number;
  note: string;
};

/** What a landed cost recorded each time it updated Shopify costs (landed_cost.shopify_costs), oldest first. */
export type AppliedCost = { item_id: string; from: number | null; to: number };

/**
 * Each Shopify item's new unit cost: the weighted average of the stock it already had at its cost before these
 * arrived and what this landed cost brought in at its landed cost, to the cent (Shopify keeps cost in cents). For an
 * item this landed cost already updated, "before" is the cost from before its first update, so running it again
 * (a late charge, a corrected rate) gives the right answer instead of averaging the average. On hand is Shopify's
 * now, so units sold since the delivery count as older stock that sold (an estimate, noted).
 */
export function shopifyCostPreview(
  alloc: Allocation,
  items: { id: string; std_cost: number | null }[],
  onHand: Map<string, number>,
  applied: AppliedCost[] = [],
): ShopifyCostRow[] {
  const byItem = new Map<string, { sku: string; name: string; qty: number; cents: number }>();
  for (const l of alloc.lines) {
    if (!l.shopify || l.landed_usd == null || !(l.qty > 0)) continue;
    const g = byItem.get(l.item_id) ?? { sku: l.sku, name: l.name, qty: 0, cents: 0 };
    g.qty += l.qty;
    g.cents += cents(l.landed_usd);
    byItem.set(l.item_id, g);
  }
  const cost = new Map(items.map((i) => [i.id, i.std_cost == null ? null : Number(i.std_cost)]));
  const money = (n: number) => `$${n.toFixed(2)}`;
  return [...byItem.entries()].map(([item_id, g]) => {
    const landed = mulRound(g.cents / 100 / g.qty, 1, 4);
    const current = cost.get(item_id) ?? null;
    const mine = applied.filter((a) => a.item_id === item_id);
    const before = mine.length ? (mine[0].from == null ? null : Number(mine[0].from)) : current;
    const on_hand = Math.max(0, Number(onHand.get(item_id) ?? 0));
    const older = Math.max(0, mulRound(on_hand - g.qty, 1, 3));
    const next = before == null || older === 0 ? mulRound(landed, 1, 2) : mulRound((mulRound(older, before, 6) + g.cents / 100) / (older + g.qty), 1, 2);
    const last = mine.at(-1);
    const note = last
      ? last.to !== current
        ? `Changed in Shopify since this landed cost set it to ${money(Number(last.to))}; this starts again from ${before == null ? "no cost" : money(before)}, the cost before it`
        : next === current
          ? "Already updated from this landed cost"
          : `Updated from this landed cost before; starts again from ${before == null ? "no cost" : money(before)}, the cost before it`
      : before == null
        ? "No cost in Shopify yet: takes the landed cost"
        : older === 0
          ? on_hand < g.qty
            ? "Fewer on hand than arrived: takes the landed cost"
            : "Nothing older in stock: takes the landed cost"
          : "";
    return {
      item_id,
      sku: g.sku,
      name: g.name,
      current,
      base: before,
      applied: mine.length > 0,
      landed_unit: landed,
      received: g.qty,
      on_hand,
      older,
      next,
      note,
    };
  });
}

// Fields ----------------------------------------------------------------------------------------------------

const text = (max = 200) => z.string().trim().max(max);
const currency = z.string().trim().toUpperCase().length(3, "Use a 3-letter currency code");

export const landedCostFields = z.object({
  forwarder: text(),
  invoice_ref: text(100),
  invoice_date: z.iso.date("Use a date like 2026-12-31").nullable(),
  goods_rates: z.record(z.string().regex(/^[A-Z]{3}$/, "Use a 3-letter currency code"), z.number().positive("A rate is more than 0").max(1_000_000)),
  notes: text(5000),
});

export const landedCostChargeFields = z.object({
  landed_cost_id: z.uuid(),
  position: z.number().int().min(0).max(10_000),
  kind: z.enum(CHARGE_KINDS),
  description: text(300),
  amount: z.number().min(-10_000_000).max(10_000_000),
  currency,
  usd_rate: z.number().positive("The rate to USD is more than 0").max(1_000_000),
  method: z.enum(ALLOCATION_METHODS),
});
